import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  ({
    label,
    placeholder = "Seleccionar opciones...",
    options,
    value = [],
    onChange,
    error,
    disabled = false,
    className = ''
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleOptionClick = (optionValue: string) => {
      if (value.includes(optionValue)) {
        // Remove the option
        onChange(value.filter(v => v !== optionValue));
      } else {
        // Add the option
        onChange([...value, optionValue]);
      }
    };

    const handleRemoveTag = (optionValue: string) => {
      onChange(value.filter(v => v !== optionValue));
    };

    const getAvailableOptions = () => {
      return options.filter(option => !value.includes(option.value));
    };

    const filteredOptions = getAvailableOptions().filter((option) => {
      if (!searchTerm.trim()) return true;
      const normalizedSearch = searchTerm.trim().toLowerCase();
      return (
        option.label.toLowerCase().includes(normalizedSearch) ||
        option.value.toLowerCase().includes(normalizedSearch)
      );
    });

    useEffect(() => {
      if (isOpen) {
        searchInputRef.current?.focus();
      } else {
        setSearchTerm('');
      }
    }, [isOpen]);

    const handleContainerClick = () => {
      if (disabled) return;
      setIsOpen(!isOpen);
    };

    const handleInputMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (!isOpen && !disabled) {
        setIsOpen(true);
      }
    };

    const inputPlaceholder = value.length === 0 ? placeholder : 'Buscar...';

    return (
      <div className={`space-y-1 ${className}`} ref={ref}>
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}

        <div className="relative" ref={dropdownRef}>
          {/* Main input container */}
          <div
            className={`
              w-full min-h-10 px-3 py-2 border rounded-md shadow-sm cursor-pointer
              focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500
              ${error ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500' : 'border-gray-300'}
              ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
            `}
            onClick={handleContainerClick}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1 flex-1">
                {value.map((selectedValue) => {
                  const option = options.find(opt => opt.value === selectedValue);
                  return (
                    <span
                      key={selectedValue}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded border"
                    >
                      {option?.label || selectedValue}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTag(selectedValue);
                          }}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  );
                })}

                {!disabled && (
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    placeholder={inputPlaceholder}
                    disabled={disabled}
                    onMouseDown={handleInputMouseDown}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => !disabled && setIsOpen(true)}
                    className="flex-1 min-w-[60px] border-none focus:ring-0 focus:outline-none text-sm placeholder-gray-400"
                  />
                )}

                {disabled && value.length === 0 && (
                  <span className="text-gray-500 text-sm">{placeholder}</span>
                )}
              </div>

              {!disabled && (
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                />
              )}
            </div>
          </div>

          {/* Dropdown */}
          {isOpen && !disabled && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {getAvailableOptions().length === 0 ? (
                <div className="px-3 py-2 text-gray-500 text-sm">
                  No hay más opciones disponibles
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-gray-500 text-sm">
                  No se encontraron resultados
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    onClick={() => handleOptionClick(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
